import { FEE_DECIMALS } from '@/features/markets/api/get-exchange-info';
import { OrderFormSchema } from '@/features/trade/order/components';
import { getIsTaker } from '@/features/trade/order/utils/helpers';
import { getSizeFromNumeraire } from '@/features/trade/order/utils/math';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS, useMarketStore } from '@/store/use-markets-store';
import { OrderType, TimeInForce } from '@/types';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import { adjustDecimals, parseDecimalToBigInt } from '@/utils/value-format';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import useLeverage from './use-leverage';
import useAvailableFunds from '@/features/account/hooks/use-available-funds';

export default function useMaxSize(symbol: string) {
  const form = useFormContext<OrderFormSchema>();

  const { midPrice, spreadPct, takerFee, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => {
      const { marketData, marketSpec, book } = state;
      let spreadPct = 0n;
      if (book.asks.length > 0 && book.bids.length > 0) {
        const spreadDiff = adjustDecimals(
          book.asks[0][0] - book.bids[0][0],
          COLLATERAL_DECIMALS - marketSpec.priceDecimals,
          'ceil',
        );
        if (marketData.midPrice > 0n) {
          spreadPct =
            (spreadDiff * 10n ** COLLATERAL_DECIMALS) /
            marketData.midPrice /
            10n ** (COLLATERAL_DECIMALS - marketSpec.priceDecimals);
        }
      }
      return {
        midPrice: marketData.midPrice,
        spreadPct,
        takerFee: marketSpec.takerFee,
        priceDecimals: marketSpec.priceDecimals,
      };
    }),
  );

  const leverage = useLeverage(symbol);

  const reduceOnly = form.watch('order.reduceOnly');
  const timeInForce = form.watch('order.timeInForce') as TimeInForce;
  const isBuy = form.watch('order.isBuy');
  const orderType = form.watch('order.orderType');
  const limitPrice = parseDecimalToBigInt(
    form.watch('order.limitPrice'),
    priceDecimals,
  );

  const price = useMemo(() => {
    switch (orderType) {
      case OrderType.LIMIT:
        return limitPrice;
      default:
        return midPrice;
    }
  }, [orderType, limitPrice, midPrice]);

  const position = useCurrentAccount((state) => {
    const position = state.positions[symbol];
    if (!position) return;
    return {
      isLong: position.isLong,
      size: position.size,
      value: position.value.bigint,
    };
  });

  const isEval = useAccountStore((state) =>
    getIsStatusEval(state.getCapitalStatus(state.address)),
  );

  const acquisitionCostPct = useMemo(() => {
    const feePct =
      getIsTaker(timeInForce) && !isEval
        ? adjustDecimals(takerFee, COLLATERAL_DECIMALS - FEE_DECIMALS)
        : 0n;
    return (feePct + spreadPct / 2n) * BigInt(leverage);
  }, [takerFee, timeInForce, spreadPct, leverage, isEval]);

  const availableFunds = useAvailableFunds(symbol, {
    leverage,
  });

  const maxSize = useMemo(() => {
    if (reduceOnly) {
      if (!position || position.isLong === isBuy) return 0n;
      return position.size;
    }

    if (price === 0n) return 0n;

    const numeraireBalance =
      (availableFunds * 10n ** COLLATERAL_DECIMALS) /
      (10n ** COLLATERAL_DECIMALS + acquisitionCostPct);

    return getSizeFromNumeraire(symbol, numeraireBalance, price, false);
  }, [
    symbol,
    availableFunds,
    reduceOnly,
    isBuy,
    position,
    price,
    acquisitionCostPct,
  ]);

  return { price, maxSize };
}
